import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { MdCheckCircle, MdPerson } from 'react-icons/md';
import type { TikTokSettingsValues } from '../integrations/detail/TikTokSettingsPanel';

interface TikTokPostSettingsCardProps {
  displayName?: string;
  username?: string;
  avatarUrl?: string;
  isVerified?: boolean;
  values: TikTokSettingsValues;
  onChange: (values: TikTokSettingsValues) => void;
}

const TikTokPostSettingsCard: React.FC<TikTokPostSettingsCardProps> = ({
  displayName,
  username,
  avatarUrl,
  isVerified,
  values,
  onChange,
}) => {
  const [isPrivacyDropdownOpen, setIsPrivacyDropdownOpen] = useState(false);
  const privacyDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (privacyDropdownRef.current && !privacyDropdownRef.current.contains(event.target as Node)) {
        setIsPrivacyDropdownOpen(false);
      }
    };

    if (isPrivacyDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isPrivacyDropdownOpen]);

  const setVisibility = (visibility: string) => onChange({ ...values, visibility });
  const setAllowComment = (allowComment: boolean) => onChange({ ...values, allowComment });
  const setAllowDuet = (allowDuet: boolean) => onChange({ ...values, allowDuet });
  const setAllowStitch = (allowStitch: boolean) => onChange({ ...values, allowStitch });
  const setDiscloseContent = (discloseContent: boolean) => onChange({ ...values, discloseContent });
  const setBrandType = (brandType: string) => onChange({ ...values, brandType });

  const { visibility, allowComment, allowDuet, allowStitch, discloseContent, brandType } = values;

  return (
    <div className="bg-[#0f0f0f] rounded-xl border border-gray-700 p-5 max-h-[40vh] overflow-auto">
      <h3 className="text-brand-primary text-sm font-normal mb-4">Default Post Settings</h3>
      {/* Profile Row */}
      <div className="flex items-cente justify-between gap-3 mb-1 ">
        <div className='flex items-cente gap-2'>
        <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 flex items-center justify-center bg-gray-700">
          {avatarUrl ? (
            <img src={avatarUrl} alt={displayName} className="w-full h-full object-cover" />
          ) : (
            <MdPerson className="text-white" size={22} />
          )}
          
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-white text-sm font-semibold truncate">{displayName}</h3>
            {isVerified && <MdCheckCircle className="text-blue-500 flex-shrink-0" size={16} />}
          </div>
          {username && <p className="text-gray-400 text-xs truncate">@{username}</p>}
        </div>
        </div>

         <div className="relative mb-2" ref={privacyDropdownRef}>
          <label className="block text-sm font-normal mb-2 text-gray-400">
            Who can view this video <span className="text-red-500">*</span>
          </label>

          <button type="button"
            onClick={() => setIsPrivacyDropdownOpen(!isPrivacyDropdownOpen)}
            className={`w-full cursor-pointer bg-[#1e1e1e] border rounded-sm p-1 text-xs flex items-center justify-between transition-colors ${
              visibility === '' ? 'border-red-500 text-gray-500' : 'border-gray-700 text-white'
            }`}
          >
            <span>{visibility || 'Select privacy level'}</span>
            <svg
              className={`w-5 h-5 transition-transform ${isPrivacyDropdownOpen ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {isPrivacyDropdownOpen && (
            <div className="absolute z-10 w-full mt-2 bg-[#1a1a1a] border border-gray-600 rounded-xl shadow-xl overflow-hidden">
              {['Public', 'Friends', 'Private'].map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => {
                    setVisibility(option);
                    setIsPrivacyDropdownOpen(false);
                  }}
                  className="w-full cursor-pointer px-4 py-3 text-left hover:bg-[#3a3a3a] transition-colors border-t border-gray-700 first:border-t-0"
                >
                  <p className="font-medium text-xs text-white">{option}</p>
                </button>
              ))}
            </div>
          )}
      </div>
      </div>

      {/* Privacy Dropdown */}
     

      {/* Allow users to */}
      <div className="mb-6">
        <label className="block text-sm font-normal mb-2 text-gray-400">Allow users to</label>
        <div className="flex flex-wrap gap-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={allowComment}
              onChange={(e) => setAllowComment(e.target.checked)}
              className="w-4 h-4 rounded bg-[#1e1e1e] border-1 border-gray-700 checked:bg-orange-500 checked:border-orange-500 cursor-pointer"
            />
            <span className="text-xs text-white">Comment</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={allowDuet}
              onChange={(e) => setAllowDuet(e.target.checked)}
              className="w-4 h-4 rounded bg-[#1e1e1e] border-1 border-gray-700 checked:bg-orange-500 checked:border-orange-500 cursor-pointer"
            />
            <span className="text-xs text-white">Duet</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={allowStitch}
              onChange={(e) => setAllowStitch(e.target.checked)}
              className="w-4 h-4 rounded bg-[#1e1e1e] border-1 border-gray-700 checked:bg-orange-500 checked:border-orange-500 cursor-pointer"
            />
            <span className="text-xs text-white">Stitch</span>
          </label>
        </div>
        <p className="text-xs text-gray-500 mt-2">All interaction controls are disabled by default. Enable as needed.</p>
      </div>

      {/* Disclose video content */}
      <div className="border-t border-gray-800 pt-4">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="font-medium text-sm mb-1 text-gray-400">Disclose video content</h3>
            <p className="text-[11px] text-gray-400">
              Turn on to disclose that this video promotes goods or services in exchange for something of value.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setDiscloseContent(!discloseContent)}
            className={`relative cursor-pointer inline-flex h-4 w-7 items-center rounded-full transition-colors flex-shrink-0 ml-4 ${
              discloseContent ? 'bg-orange-500' : 'bg-gray-700'
            }`}
          >
            <span
              className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                discloseContent ? 'translate-x-3.5' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        {discloseContent && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="space-y-3 mt-4"
          >
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-sm p-2">
              <p className="text-[12px] text-blue-300">
                Your video will be labeled "Promotional content". This cannot be changed once your video is posted.
              </p>
            </div>

            <label className="flex items-start gap-3 cursor-pointer p-1 rounded-xl hover:bg-[#1e1e1e] transition-colors">
              <input
                type="radio"
                name="postNowBrandType"
                value="your-brand"
                checked={brandType === 'your-brand'}
                onChange={(e) => setBrandType(e.target.value)}
                className="mt-1 w-3 h-3 text-orange-500"
              />
              <div>
                <p className="font-medium text-sm mb-1 text-gray-400">Your brand</p>
                <p className="text-[11px] text-gray-300">
                  You are promoting yourself or your own business. This video will be classified as Brand Organic.
                </p>
              </div>
            </label>

            <label className="flex items-start gap-3 cursor-pointer p-1 rounded-xl hover:bg-[#1e1e1e] transition-colors">
              <input
                type="radio"
                name="postNowBrandType"
                value="branded-content"
                checked={brandType === 'branded-content'}
                onChange={(e) => setBrandType(e.target.value)}
                className="mt-1 w-3 h-3 text-orange-500"
              />
              <div>
                <p className="font-medium text-sm mb-1 text-gray-400">Branded content</p>
                <p className="text-[11px] text-gray-300">
                  You are promoting another brand or a third party. This video will be classified as Branded Content.
                </p>
              </div>
            </label>
          </motion.div>
        )}
      </div>

      {/* Music Usage Consent */}
      <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-sm p-2 mt-6">
        <p className="text-[11px] text-yellow-200 text-center">
          By posting, you agree to TikTok's{' '}
          <a
            href="https://www.tiktok.com/legal/music-usage-confirmation"
            target="_blank"
            rel="noopener noreferrer"
            className="text-orange-400 hover:underline font-medium"
          >
            Music Usage Confirmation
          </a>
        </p>
      </div>
    </div>
  );
};

export default TikTokPostSettingsCard;
